// Used for sales based billing
import { number } from '../../util/formatters'
import { Interval } from './Plan'

export interface BillingPlan {
    id: string
    name: string
    description: string
    // in dollars
    monthly: number
    // in dollars
    yearly: number
    // the % of sales to charge on, as a decimal. So 1.2% = 0.012
    rate: number
    // in dollars
    included_sales: number
    included_ads: number
    // how many additional ads are included in a pack
    ad_pack_amount: number
    // in dollars
    ad_pack_price: number
    historical_data: string
    syncing: string
    automations: string
}

export const EssentialsPlanV1: BillingPlan = {
    id: 'essentials_v1',
    name: 'Essentials',
    description: 'For businesses starting their advertising journey',
    monthly: 15,
    yearly: 120,
    rate: 0.012,
    included_sales: 1250,
    included_ads: 1000,
    ad_pack_amount: 100000,
    ad_pack_price: 10,
    historical_data: '1 year of historical data',
    syncing: '1x data sync per day',
    automations: '1x automation run per day',
}

export const StandardPlanV1: BillingPlan = {
    id: 'standard_v1',
    name: 'Standard',
    description: 'For growing and established advertisers',
    monthly: 75,
    yearly: 600,
    rate: 0.01,
    included_sales: 7500,
    included_ads: 250000,
    ad_pack_amount: 250000,
    ad_pack_price: 20,
    historical_data: '2 years of historical data',
    syncing: '2x data syncs per day',
    automations: '2x automation runs per day',
}

export const PremiumPlanV1: BillingPlan = {
    id: 'premium_v1',
    name: 'Premium',
    description: 'For larger advertisers',
    monthly: 400,
    yearly: 3180,
    rate: 0.008,
    included_sales: 50000,
    included_ads: 2000000,
    ad_pack_amount: 250000,
    ad_pack_price: 20,
    historical_data: 'Unlimited historical data',
    syncing: '4x data syncs per day',
    automations: '4x automation runs per day',
}

export interface SalesBasedPlan {
    id: string
    name: string
    description: string
    monthly: number
    yearly: number
    included_spend: number
    ad_spend_rates: {
        [Interval.Monthly]: number
        [Interval.Yearly]: number
    }
    historical_data: string
    syncing: string
    automations: string
    included_ads: number
    ads_overage?: {
        /** The number of extra ads included */
        amount: number
        /** The cost of this extra ad pack, in dollars */
        price: number
    }

    included_ad_accounts: number
    ad_account_overage?: {
        /** The cost of an extra ad account, in dollars */
        price: number
    }
}

export const EssentialsPlanV2: SalesBasedPlan = {
    id: 'essentials_v2',
    name: 'Essentials',
    description: 'For businesses starting their advertising journey',
    monthly: 120,
    yearly: 948,
    included_spend: 2000,
    ad_spend_rates: {
        [Interval.Monthly]: 6.0 / 100,
        [Interval.Yearly]: 4.0 / 100,
    },
    historical_data: '1 year of historical data',
    syncing: '1x data sync per day',
    automations: '1x automation run per day',
    included_ads: 10000,

    included_ad_accounts: 10,
}

export const StandardPlanV2: SalesBasedPlan = {
    id: 'standard_v2',
    name: 'Standard',
    description: 'For growing and established advertisers',
    monthly: 300,
    yearly: 2388,
    included_spend: 7500,
    ad_spend_rates: {
        [Interval.Monthly]: 4.0 / 100,
        [Interval.Yearly]: 3.0 / 100,
    },
    historical_data: '2 years of historical data',
    syncing: '2x data syncs per day',
    automations: '2x automation runs per day',
    included_ads: 100000,
    ads_overage: {
        amount: 100000,
        price: 20.0,
    },

    included_ad_accounts: 30,
    ad_account_overage: {
        price: 5.0,
    },
}

export const PremiumPlanV2: SalesBasedPlan = {
    id: 'premium_v2',
    name: 'Premium',
    description: 'For larger advertisers',
    monthly: 600,
    yearly: 4788,
    included_spend: 20000,
    ad_spend_rates: {
        [Interval.Monthly]: 2.0 / 100,
        [Interval.Yearly]: 2.0 / 100,
    },
    historical_data: 'Unlimited historical data',
    syncing: '4x data syncs per day',
    automations: '4x automation runs per day',
    included_ads: 500000,
    ads_overage: {
        amount: 100000,
        price: 20.0,
    },

    included_ad_accounts: 50,
    ad_account_overage: {
        price: 5.0,
    },
}
